import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function FeesPage() {
  const [activeCategory, setActiveCategory] = useState(0);

  useEffect(() => {
    document.title = 'Nevada DMV Fees & Pricing Las Vegas | DMV-Xpress Services — Vehicle Registration Costs';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Nevada DMV fee breakdown for Las Vegas (Clark County) — vehicle registration fees, title transfer costs, license plate fees, temporary permit fees. Based on dmv.nv.gov 2024-2025 rates.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}/fees`);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.setAttribute('data-page-schema', 'fees');
    schema.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/fees#webpage`,
        url: `${import.meta.env.VITE_SITE_URL || ''}/fees`,
        name: 'Nevada DMV Fees & Pricing Las Vegas | DMV-Xpress Services',
        description: 'Nevada DMV fee breakdown for Las Vegas (Clark County) — vehicle registration fees, title transfer costs, license plate fees, temporary permit fees.',
        isPartOf: { '@type': 'WebSite', url: import.meta.env.VITE_SITE_URL || '' },
        about: {
          '@type': 'Service',
          name: 'Nevada DMV Document Preparation Services',
          provider: {
            '@type': 'LocalBusiness',
            '@id': `${import.meta.env.VITE_SITE_URL || ''}/#business`,
            name: 'DMV-Xpress Services',
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'PriceSpecification',
        name: 'Nevada Vehicle Registration Base Fee',
        price: '33.00',
        priceCurrency: 'USD',
        description: 'Base registration fee for most passenger vehicles in Nevada. Additional Governmental Services Tax (GST) applies based on vehicle value.',
        eligibleRegion: { '@type': 'State', name: 'Nevada' },
      },
    ]);
    document.head.appendChild(schema);
    return () => {
      const existing = document.head.querySelector('script[data-page-schema="fees"]');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const feeCategories = [
    {
      label: 'Vehicle Registration', icon: 'ri-car-line',
      fees: [
        { item: 'Base Registration Fee (cars, trucks under 6,000 lbs, motorcycles)', amount: '$33.00', note: 'Per Nevada DMV — annual' },
        { item: 'Governmental Services Tax (GST)', amount: 'Variable', note: '4¢ per $1 of depreciated vehicle value (35% of original MSRP)' },
        { item: 'Supplemental GST — Clark County', amount: 'Variable', note: 'Additional 1¢ per $1 of depreciated value (Clark County only)' },
        { item: 'Motorcycle Safety Fee', amount: '$6.00', note: 'Added to all motorcycle registrations' },
        { item: 'Trailer Registration (under 1,000 lbs)', amount: '$12.00', note: 'Annual flat fee' },
        { item: 'Trailer Registration (1,000–6,000 lbs)', amount: '$18.00', note: 'Annual flat fee' },
        { item: 'Travel Trailer Registration', amount: '$27.00', note: 'Annual flat fee' },
        { item: 'Late Registration Penalty', amount: '$10.00+', note: 'No grace period — penalty applies immediately after expiration' },
        { item: 'VIN Inspection Fee (out-of-state vehicles)', amount: '$1.00', note: 'State fee — done at Nevada DMV office without appointment' },
        { item: 'Emissions Test (Clark County)', amount: 'Varies', note: 'Paid to emissions testing station — not included in DMV fees' },
      ]
    },
    {
      label: 'Title & Transfer', icon: 'ri-file-transfer-line',
      fees: [
        { item: 'Title Transfer (Change of Ownership)', amount: '$28.25', note: 'Per Nevada DMV — required for all private party and dealer sales' },
        { item: 'New Nevada Title (No change in ownership)', amount: '$20.00', note: 'For vehicles being titled in Nevada for the first time without ownership change' },
        { item: 'Duplicate Title (sent to Nevada address)', amount: '$20.00', note: 'Replacement for lost, stolen, or damaged Nevada title' },
        { item: 'Duplicate Title (sent out of state)', amount: '$35.00', note: 'Higher fee for out-of-state delivery' },
        { item: 'Lien Notation on Title', amount: '$8.25', note: 'Adding a lender/lienholder to the Nevada title' },
        { item: 'Lien Release on Title', amount: '$8.25', note: 'Removing a paid-off lender from the Nevada title' },
        { item: 'Title Correction', amount: '$20.00', note: 'Correcting errors on an existing Nevada title' },
        { item: 'Odometer Disclosure Statement', amount: 'Included', note: 'Required for vehicles under 10 years old — no separate fee' },
        { item: 'Notarization (in-office at DMV-Xpress)', amount: 'Ask at appt.', note: 'Our notary fee — separate from Nevada DMV state fees' },
      ]
    },
    {
      label: 'License Plates', icon: 'ri-bank-card-line',
      fees: [
        { item: 'Standard Nevada Plates (pair)', amount: '$7.00', note: 'Issued at time of registration — most passenger vehicles' },
        { item: 'Plate Transfer Fee (existing plates to new vehicle)', amount: '$6.00', note: 'Moving your current plates to a different vehicle' },
        { item: 'Specialty / Organization Plates (initial)', amount: '$36–$71', note: 'Varies by plate type — initial issuance fee' },
        { item: 'Specialty Plate Annual Renewal', amount: '$10–$30', note: 'Annual renewal fee for specialty plates — varies by type' },
        { item: 'Personalized / Vanity Plates (initial)', amount: '$36–$71', note: 'Plus standard plate fees — availability check required' },
        { item: 'Personalized Plate Annual Renewal', amount: '$10–$30', note: 'Annual renewal fee for personalized plates' },
        { item: 'Replacement Plates (lost/stolen/damaged)', amount: '$7.00', note: 'Replacement pair — police report required for stolen plates' },
        { item: 'Veteran / Military Plates', amount: 'Varies', note: 'Some veteran plates are issued at no charge — eligibility required' },
      ]
    },
    {
      label: 'Temporary Permits', icon: 'ri-time-line',
      fees: [
        { item: '10-Day Temporary Movement Permit', amount: 'Varies', note: 'For vehicles already registered in Nevada — allows movement within state' },
        { item: '30-Day Temporary Movement Permit', amount: 'Varies', note: 'For vehicles NOT registered in Nevada — new residents and buyers' },
        { item: 'In-Transit Permit', amount: 'Varies', note: 'For moving a vehicle through Nevada to another state' },
        { item: 'Dealer Transport Tag', amount: 'Varies', note: 'For licensed Nevada dealers transporting vehicles' },
        { item: 'DMV-Xpress Service Fee', amount: 'Ask at appt.', note: 'Our document preparation fee — separate from Nevada DMV state fees' },
      ]
    },
    {
      label: 'Notary Services', icon: 'ri-quill-pen-line',
      fees: [
        { item: 'Title Notarization', amount: 'Ask at appt.', note: 'Required for most Nevada vehicle title transfers — both parties must be present' },
        { item: 'Bill of Sale Notarization', amount: 'Ask at appt.', note: 'Notarized bill of sale for vehicle purchases' },
        { item: 'Affidavit Notarization', amount: 'Ask at appt.', note: 'Lost title affidavit, estate affidavit, and other DMV affidavits' },
        { item: 'Power of Attorney Notarization', amount: 'Ask at appt.', note: 'For title transfers where one party cannot be present' },
        { item: 'General Document Notarization', amount: 'Ask at appt.', note: 'Other DMV-related documents requiring notarization' },
      ]
    },
  ];

  const importantNotes = [
    { icon: 'ri-information-fill', title: 'DMV Fees vs. Our Service Fee', desc: 'Nevada DMV state fees are paid directly to the state. DMV-Xpress charges a separate document preparation service fee. We will provide the exact total at your appointment.' },
    { icon: 'ri-calculator-fill', title: 'GST Calculation', desc: 'The Governmental Services Tax (GST) is the largest variable in Nevada registration costs. It\'s based on 35% of your vehicle\'s original MSRP, depreciated by age. A newer, more expensive vehicle pays significantly more GST.' },
    { icon: 'ri-map-pin-fill', title: 'Clark County Supplemental GST', desc: 'Las Vegas (Clark County) residents pay an additional 1¢ per $1 of depreciated vehicle value on top of the state GST. This is a Clark County-specific fee.' },
    { icon: 'ri-alert-fill', title: 'No Grace Period for Late Registration', desc: 'Nevada has NO grace period for expired vehicle registration. Late fees apply immediately after the expiration date. Don\'t wait — call DMV-Xpress to renew before your registration expires.' },
    { icon: 'ri-phone-fill', title: 'No Fee Quotes by Phone', desc: 'We cannot provide exact fee quotes by phone or chat. Nevada DMV fees vary significantly by vehicle. We calculate the exact amount at your appointment using the Nevada DMV fee schedule.' },
    { icon: 'ri-external-link-fill', title: 'Official Fee Estimator', desc: 'For an estimate of Nevada DMV fees for your specific vehicle, use the official Nevada DMV Registration Fee Estimate tool at dmv.nv.gov.' },
  ];

  const isVariable = (amount: string) => ['Variable', 'Varies', 'Ask at appt.', 'Included'].includes(amount);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Source Sans Pro', 'Segoe UI', sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: 320 }}>
        <img
          src="https://public.readdy.ai/ai/img_res/6187ecccfca43c4d8b516b9b09ed1c43.jpg"
          alt="Nevada DMV Fees & Pricing Las Vegas — DMV-Xpress Services"
          className="w-full h-full object-cover absolute inset-0"
          style={{ minHeight: 320 }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,40,85,0.92) 0%, rgba(0,40,85,0.75) 55%, rgba(0,40,85,0.4) 100%)' }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded mb-5 uppercase tracking-widest" style={{ backgroundColor: '#E87722' }}>
            <i className="ri-money-dollar-circle-fill"></i>Based on Nevada DMV Official Fee Schedule — dmv.nv.gov
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
            Nevada DMV <span style={{ color: '#E87722' }}>Fees &amp; Pricing</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-6">
            Official Nevada DMV fee breakdown for Las Vegas (Clark County) — vehicle registration, title transfers, license plates, temporary permits, and notary services.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:7028028696" className="inline-flex items-center justify-center gap-2 text-white font-bold px-8 py-3.5 rounded text-base transition-all whitespace-nowrap" style={{ backgroundColor: '#E87722' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
              <i className="ri-phone-fill"></i>Call for Exact Fees: 702.802.8696
            </a>
            <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-bold px-8 py-3.5 rounded text-base hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap">
              <i className="ri-external-link-line"></i>Official Nevada DMV Fee Estimator
            </a>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <div className="w-full py-3 border-b" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-2 text-center text-sm">
          <i className="ri-information-fill flex-shrink-0" style={{ color: '#E87722' }}></i>
          <p className="text-gray-700">
            <strong style={{ color: '#003366' }}>DMV-Xpress is NOT the Nevada DMV.</strong> Nevada DMV state fees are paid to the state — our service fee is separate. Fees listed are Nevada DMV rates and may change.{' '}
            <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold" style={{ color: '#003366' }}>Nevada DMV fee estimator →</a>
          </p>
        </div>
      </div>

      {/* ── FEE TABLES ── */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            Fee Breakdown by Service Type.
          </h2>
          <p className="text-gray-600 mt-4 mb-8 text-base max-w-2xl">
            Select a service category to view the Nevada DMV fee schedule. All fees are per dmv.nv.gov — Clark County / Las Vegas rates.
          </p>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {feeCategories.map((cat, i) => (
              <button key={i} onClick={() => setActiveCategory(i)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
                style={{
                  backgroundColor: activeCategory === i ? '#003366' : '#f1f5f9',
                  color: activeCategory === i ? '#fff' : '#475569',
                  border: activeCategory === i ? '1px solid #003366' : '1px solid #e5e7eb',
                }}>
                <i className={`${cat.icon} text-sm`} style={{ color: activeCategory === i ? '#E87722' : '#94a3b8' }}></i>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Fee Table */}
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <div className="px-6 py-4 border-b flex items-center justify-between flex-wrap gap-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#003366' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E87722' }}>
                  <i className={`${feeCategories[activeCategory].icon} text-white text-base`}></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-base">{feeCategories[activeCategory].label} — Nevada DMV Fees</h3>
                  <p className="text-white/50 text-xs">Clark County / Las Vegas — per dmv.nv.gov 2024/2025</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'rgba(232,119,34,0.2)', border: '1px solid rgba(232,119,34,0.4)' }}>
                <i className="ri-information-line text-sm" style={{ color: '#E87722' }}></i>
                <span className="text-xs font-semibold" style={{ color: '#E87722' }}>State fees only — DMV-Xpress service fee is separate</span>
              </div>
            </div>
            {/* Table Header */}
            <div className="grid grid-cols-12 px-6 py-3 border-b" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
              <div className="col-span-7 text-gray-400 text-xs font-bold uppercase tracking-wider">Fee Item</div>
              <div className="col-span-2 text-gray-400 text-xs font-bold uppercase tracking-wider text-center">Amount</div>
              <div className="col-span-3 text-gray-400 text-xs font-bold uppercase tracking-wider">Notes</div>
            </div>
            <div className="divide-y" style={{ borderColor: '#f3f4f6' }}>
              {feeCategories[activeCategory].fees.map((fee, i) => (
                <div key={i} className="grid grid-cols-12 px-6 py-4 hover:bg-gray-50 transition-colors items-start">
                  <div className="col-span-7 pr-4">
                    <p className="text-gray-800 text-sm font-medium leading-snug">{fee.item}</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className={`text-sm font-bold px-3 py-1 rounded-full ${isVariable(fee.amount) ? 'text-gray-500 bg-gray-100' : 'text-white'}`}
                      style={isVariable(fee.amount) ? {} : { backgroundColor: '#003366' }}>
                      {fee.amount}
                    </span>
                  </div>
                  <div className="col-span-3 pl-2">
                    <p className="text-gray-400 text-xs leading-relaxed">{fee.note}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
              <p className="text-gray-600 text-sm">
                <strong style={{ color: '#003366' }}>Need exact fees for your vehicle?</strong> Call <a href="tel:7028028696" className="font-semibold" style={{ color: '#E87722' }}>702.802.8696</a> — we calculate the exact Nevada DMV fees at your appointment.
              </p>
              <a href="tel:7028028696" className="inline-flex items-center gap-2 text-white font-bold px-5 py-2.5 rounded text-sm transition-all whitespace-nowrap flex-shrink-0" style={{ backgroundColor: '#E87722' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
                <i className="ri-phone-fill"></i>Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── GST EXPLAINER ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            How Nevada Calculates Registration Fees.
          </h2>
          <p className="text-gray-600 mt-4 mb-10 text-base max-w-2xl">
            The Governmental Services Tax (GST) is the biggest variable in your Nevada registration cost. Here's how it works.
          </p>
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {[
              { step: '1', title: 'Original MSRP', desc: 'Nevada uses the original Manufacturer\'s Suggested Retail Price (MSRP) of your vehicle — not what you paid for it. A $50,000 vehicle uses $50,000 as the base, even if you bought it used for $20,000.' },
              { step: '2', title: 'Depreciated Value', desc: 'Nevada applies a depreciation schedule — 35% of the original MSRP is used as the "depreciated value" for GST calculation. This value decreases each year as the vehicle ages.' },
              { step: '3', title: 'GST Rate Applied', desc: 'The state GST rate is 4¢ per $1 of depreciated value. Clark County adds an additional 1¢ per $1. So Las Vegas residents pay 5¢ per $1 of depreciated value total.' },
            ].map((s, i) => (
              <div key={i} className="bg-white p-6 rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 text-white font-black text-lg" style={{ backgroundColor: '#003366' }}>{s.step}</div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Example Calculation */}
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <div className="p-5 border-b" style={{ borderColor: '#e5e7eb', backgroundColor: '#003366' }}>
              <h3 className="text-white font-bold text-base">Example: 2022 Toyota Camry — Original MSRP $28,000 — Clark County</h3>
              <p className="text-white/50 text-xs mt-1">Illustrative example only — actual fees vary. Use Nevada DMV fee estimator for your vehicle.</p>
            </div>
            <div className="divide-y" style={{ borderColor: '#f3f4f6' }}>
              {[
                { label: 'Original MSRP', value: '$28,000', note: 'Manufacturer\'s Suggested Retail Price' },
                { label: 'Depreciated Value (35% of MSRP)', value: '$9,800', note: '35% × $28,000 = $9,800' },
                { label: 'State GST (4¢ per $1)', value: '$392.00', note: '$9,800 × 0.04 = $392.00' },
                { label: 'Clark County Supplemental GST (1¢ per $1)', value: '$98.00', note: '$9,800 × 0.01 = $98.00' },
                { label: 'Base Registration Fee', value: '$33.00', note: 'Flat fee for passenger vehicles' },
                { label: 'Standard Plates (if new)', value: '$7.00', note: 'New plate issuance' },
                { label: 'Estimated Total Nevada DMV Fees', value: '~$530', note: 'Approximate — does not include emissions test or DMV-Xpress service fee', highlight: true },
              ].map((row, i) => (
                <div key={i} className={`grid grid-cols-12 px-6 py-4 items-center ${row.highlight ? 'border-t-2' : ''}`}
                  style={{ backgroundColor: row.highlight ? '#FFF8F0' : i % 2 === 0 ? '#fff' : '#fafafa', borderColor: row.highlight ? '#E87722' : undefined }}>
                  <div className="col-span-5">
                    <p className={`text-sm font-medium ${row.highlight ? 'font-bold' : ''}`} style={{ color: row.highlight ? '#E87722' : '#374151' }}>{row.label}</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className={`text-sm font-bold ${row.highlight ? 'text-lg' : ''}`} style={{ color: row.highlight ? '#E87722' : '#003366' }}>{row.value}</span>
                  </div>
                  <div className="col-span-5 pl-4">
                    <p className="text-gray-400 text-xs">{row.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── IMPORTANT NOTES ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            Important Fee Notes.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {importantNotes.map((note, i) => (
              <div key={i} className="p-6 rounded-lg border hover:border-orange-300 transition-all" style={{ borderColor: '#e5e7eb' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#EEF4FF' }}>
                  <i className={`${note.icon} text-lg`} style={{ color: '#003366' }}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{note.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{note.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 relative overflow-hidden" style={{ backgroundColor: '#003366' }}>
        <div className="absolute top-0 left-0 right-0 h-8 overflow-hidden">
          <svg viewBox="0 0 1440 32" preserveAspectRatio="none" className="w-full h-full" style={{ fill: '#fff' }}>
            <path d="M0,0 C360,32 1080,32 1440,0 L1440,0 L0,0 Z"></path>
          </svg>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-8">
          <h2 className="text-3xl font-black text-white mb-4 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block' }}>
            Ready to Get Your Nevada DMV Transaction Done?
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto mt-4">
            Call us to schedule your appointment. We'll calculate the exact Nevada DMV fees for your vehicle and tell you exactly what to bring.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:7028028696" className="inline-flex items-center justify-center gap-3 text-white px-10 py-4 rounded font-bold transition-all text-base whitespace-nowrap" style={{ backgroundColor: '#E87722' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
              <i className="ri-phone-fill text-xl"></i>Call 702.802.8696
            </a>
            <Link to="/faq" className="inline-flex items-center justify-center gap-3 border-2 border-white/40 text-white px-10 py-4 rounded font-bold hover:bg-white hover:text-gray-900 transition-all text-base whitespace-nowrap">
              <i className="ri-question-answer-line text-xl"></i>View FAQ
            </Link>
          </div>
          <p className="text-white/40 text-sm mt-6">By appointment only — Mon–Fri 9AM–5PM, Sat 10AM–3PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
